import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, List, Map as MapIcon, ShieldCheck, Clock, Zap } from "lucide-react";
import { ProviderCard } from "@/components/ProviderCard";
import { MapView } from "@/components/MapView";
import { DispatchModal } from "@/components/DispatchModal";
import { CATEGORY_META, CATEGORY_ORDER } from "@/lib/constants";
import { toast } from "sonner";

export default function Home() {
  const [providers, setProviders] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [view, setView] = useState("list");
  const [dispatchProvider, setDispatchProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get("view") === "map") setView("map");
  }, [searchParams]);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (q) params.q = q;
    const { data } = await api.get("/providers", { params });
    setProviders(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const onCall = (p) => { window.location.href = `tel:${p.phone}`; };

  const featured = useMemo(() => providers.filter((p) => p.tier === "gold").slice(0, 3), [providers]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1632733711679-529326f6db12?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-[#0B0F17]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-3xl">
            <span className="font-mono-accent text-xs uppercase tracking-widest text-amber-400 flex items-center gap-2">
              <Zap size={14} /> Mobile vehicle services · call-out anywhere
            </span>
            <h1 className="font-head font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-4 text-slate-50">
              Trusted mobile mechanics<br />that come <span className="text-amber-500">to you</span>.
            </h1>
            <p className="text-slate-300 mt-4 text-base sm:text-lg max-w-xl">
              Find vetted mobile mechanics, valeters, tyre repairs, auto electricians and recovery near you — free for customers, always.
            </p>

            {/* Search */}
            <div className="mt-7 bg-slate-900/80 backdrop-blur border border-slate-800 rounded-2xl p-3 flex flex-col sm:flex-row gap-2 shadow-2xl">
              <div className="flex items-center gap-2 flex-1 bg-slate-800/70 rounded-xl px-3">
                <MapPin size={18} className="text-amber-500 shrink-0" />
                <Input data-testid="hero-search-location-input" value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="Town, postcode or service..."
                  className="border-0 bg-transparent focus-visible:ring-0 text-slate-100 placeholder:text-slate-500" />
              </div>
              <Button data-testid="hero-search-submit-button" onClick={load}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl h-11 px-6">
                <Search size={18} className="mr-1.5" /> Search
              </Button>
            </div>

            <div className="flex items-center gap-4 mt-5 text-xs text-slate-400">
              <span className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-emerald-400" /> Vetted pros</span>
              <span className="flex items-center gap-1.5"><Clock size={15} className="text-amber-400" /> Fast response</span>
              <span className="flex items-center gap-1.5"><Zap size={15} className="text-blue-400" /> Free to use</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
          <Chip active={category === ""} onClick={() => setCategory("")} label="All Services" />
          {CATEGORY_ORDER.map((id) => {
            const m = CATEGORY_META[id];
            return (
              <Chip key={id} testid={`category-chip-${id}`} active={category === id}
                onClick={() => setCategory(id)} label={m.short} Icon={m.Icon} />
            );
          })}
        </div>

        {/* View toggle + count */}
        <div className="flex items-center justify-between mt-6 mb-4">
          <h2 className="font-head font-extrabold text-2xl text-slate-50">
            {loading ? "Loading..." : `${providers.length} providers`}
            {category && <span className="text-amber-500"> · {CATEGORY_META[category].short}</span>}
          </h2>
          <div className="flex bg-slate-900 border border-slate-800 rounded-xl p-1">
            <button onClick={() => setView("list")} data-testid="view-toggle-list"
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-amber-500 text-slate-950" : "text-slate-400"}`}>
              <List size={16} /> List
            </button>
            <button onClick={() => setView("map")} data-testid="view-toggle-map"
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${view === "map" ? "bg-amber-500 text-slate-950" : "text-slate-400"}`}>
              <MapIcon size={16} /> Map
            </button>
          </div>
        </div>

        {view === "map" ? (
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <MapView providers={providers} onSelect={(p) => setDispatchProvider(p)} />
            </div>
            <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
              {providers.map((p, i) => (
                <ProviderCard key={p.id} provider={p} index={i}
                  onCall={onCall} onRequest={setDispatchProvider} onOpen={() => navigate(`/provider/${p.id}`)} />
              ))}
            </div>
          </div>
        ) : (
          <>
            {providers.length === 0 && !loading && (
              <div className="text-center py-16 text-slate-500">
                <MapPin size={40} className="mx-auto mb-3 opacity-40" />
                No providers found. Try a different search or category.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {providers.map((p, i) => (
                <ProviderCard key={p.id} provider={p} index={i}
                  onCall={onCall} onRequest={setDispatchProvider} onOpen={() => navigate(`/provider/${p.id}`)} />
              ))}
            </div>
          </>
        )}
      </div>

      <DispatchModal provider={dispatchProvider} open={!!dispatchProvider} onClose={() => setDispatchProvider(null)} />
    </div>
  );
}

const Chip = ({ label, Icon, active, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${active ? "bg-amber-500 text-slate-950 border-amber-500" : "bg-slate-900 text-slate-300 border-slate-800 hover:border-amber-500/50"}`}>
    {Icon && <Icon size={15} />} {label}
  </button>
);
